/**
 * Helpers for detecting and resolving program-level mandatory optional fields
 * that the user has not yet filled.
 *
 * Each program's `userOptionalFields[fieldName]` is a per-role config:
 *   - presence of the role key means the field is enabled for that role
 *   - value === true means the field is mandatory for that role
 *
 * A field counts as "mandatory for the user" if, in ANY program the user
 * belongs to, the field is configured as `true` for that user's role in
 * that program.
 */

type Role = 'mentor' | 'mentee' | 'admin';
type RoleConfig = Partial<Record<Role, boolean>>;
export type UserOptionalFields = Record<string, RoleConfig>;

export interface UserGroupInfo {
  grpId: string;
  role: Role | string;
  userOptionalFields?: UserOptionalFields;
}

/**
 * Where each optional field lives on the user profile, how to read it, and
 * the kind of input the modal should render.
 */
export type FieldInputKind =
  | 'text'
  | 'textarea'
  | 'date'
  | 'tel'
  | 'url'
  | 'select'
  | 'multiselect'
  | 'image'
  | 'externalModal';

export interface FieldMeta {
  /** Key in `userOptionalFields` (matches BE OPTIONAL_FIELDS constant) */
  name: string;
  /** User-facing label */
  label: string;
  /** What input to render */
  kind: FieldInputKind;
  /** Returns true if the user has filled this field */
  isFilled: (user: any) => boolean;
  /**
   * For inline inputs: returns the current value from user profile to seed the form.
   * Optional - text/select default to '' if absent.
   */
  getInitialValue?: (user: any) => any;
  /** Static options for select/multiselect. For 'country' this is loaded dynamically. */
  options?: { label: string; value: string }[];
  /** For multiselect with freeSolo (hobbies/disability) */
  freeSolo?: boolean;
  /**
   * For 'externalModal' fields, label of the action button (e.g. "Add work history").
   * The modal will dispatch the corresponding action; the trigger re-checks
   * after the user closes the external modal.
   */
  externalAction?: 'workHistory' | 'educationHistory' | 'learningObjectives' | 'socials' | 'subjects' | 'memberAttributes';
  /**
   * Helper text shown under the input.
   */
  helper?: string;
}

const isNonEmptyString = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '';
const isNonEmptyArray = (v: unknown): boolean => Array.isArray(v) && v.length > 0;

const GENDER_OPTIONS = [
  { label: 'Prefer not to say', value: 'Prefer not to say' },
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
  { label: 'Non-binary', value: 'Non-binary' }
];

const ETHNICITY_OPTIONS = [
  { label: 'Prefer not to say', value: 'Prefer not to say' },
  { label: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
  { label: 'Asian', value: 'Asian' },
  { label: 'Black or African American', value: 'Black or African American' },
  { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
  { label: 'Native Hawaiian or Other Pacific Islander', value: 'Native Hawaiian or Other Pacific Islander' },
  { label: 'White', value: 'White' }
];

const FIRST_GEN_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' }
];

const DISABILITY_OPTIONS = [
  { label: 'None', value: 'None' },
  { label: 'Physical', value: 'Physical' },
  { label: 'Cognitive', value: 'Cognitive' },
  { label: 'Sensory', value: 'Sensory' },
  { label: 'Mental health', value: 'Mental health' },
  { label: 'Prefer not to say', value: 'Prefer not to say' }
];

export const FIELD_META: Record<string, FieldMeta> = {
  bio: {
    name: 'bio',
    label: 'Biography',
    kind: 'textarea',
    isFilled: (u) => isNonEmptyString(u?.personal_details?.bio),
    getInitialValue: (u) => u?.personal_details?.bio ?? ''
  },
  phoneNumber: {
    name: 'phoneNumber',
    label: 'Phone number',
    kind: 'tel',
    isFilled: (u) => isNonEmptyString(u?.personal_details?.phoneNumber),
    getInitialValue: (u) => u?.personal_details?.phoneNumber ?? ''
  },
  dob: {
    name: 'dob',
    label: 'Date of birth',
    kind: 'date',
    isFilled: (u) => isNonEmptyString(u?.personal_details?.dob),
    getInitialValue: (u) => u?.personal_details?.dob ?? ''
  },
  gender: {
    name: 'gender',
    label: 'Gender',
    kind: 'select',
    options: GENDER_OPTIONS,
    isFilled: (u) => isNonEmptyString(u?.personal_details?.gender),
    getInitialValue: (u) => u?.personal_details?.gender ?? ''
  },
  ethnicity: {
    name: 'ethnicity',
    label: 'Ethnicity',
    kind: 'select',
    options: ETHNICITY_OPTIONS,
    isFilled: (u) => isNonEmptyString(u?.personal_details?.ethnicity),
    getInitialValue: (u) => u?.personal_details?.ethnicity ?? ''
  },
  firstGenerationStudent: {
    name: 'firstGenerationStudent',
    label: 'First generation student?',
    kind: 'select',
    options: FIRST_GEN_OPTIONS,
    isFilled: (u) => {
      const v = u?.personal_details?.firstGenStudent;
      return isNonEmptyString(v);
    },
    getInitialValue: (u) => u?.personal_details?.firstGenStudent ?? ''
  },
  country: {
    name: 'country',
    label: 'Country',
    kind: 'select',
    isFilled: (u) => isNonEmptyString(u?.personal_details?.country),
    getInitialValue: (u) => u?.personal_details?.country ?? ''
  },
  disabilityType: {
    name: 'disabilityType',
    label: 'Disability type',
    kind: 'multiselect',
    options: DISABILITY_OPTIONS,
    isFilled: (u) => isNonEmptyArray(u?.personal_details?.disability),
    getInitialValue: (u) => (Array.isArray(u?.personal_details?.disability) ? u.personal_details.disability : [])
  },
  hobbiesInterests: {
    name: 'hobbiesInterests',
    label: 'Hobbies & interests',
    kind: 'multiselect',
    freeSolo: true,
    isFilled: (u) => isNonEmptyArray(u?.personal_details?.hobbies),
    getInitialValue: (u) => (Array.isArray(u?.personal_details?.hobbies) ? u.personal_details.hobbies : []),
    helper: 'Type and press Enter to add'
  },
  department: {
    name: 'department',
    label: 'Department',
    kind: 'multiselect',
    isFilled: (u) => isNonEmptyArray(u?.departments),
    getInitialValue: (u) => (Array.isArray(u?.departments) ? u.departments : [])
  },
  videoIntro: {
    name: 'videoIntro',
    label: 'Video intro URL',
    kind: 'url',
    isFilled: (u) => isNonEmptyString(u?.videoIntro),
    getInitialValue: (u) => u?.videoIntro ?? ''
  },
  companyName: {
    name: 'companyName',
    label: 'Current company',
    kind: 'text',
    isFilled: (u) => isNonEmptyString(u?.bio?.workHistory?.companyName),
    getInitialValue: (u) => u?.bio?.workHistory?.companyName ?? ''
  },
  companyInfo: {
    name: 'companyInfo',
    label: 'Company information',
    kind: 'textarea',
    isFilled: (u) => isNonEmptyString(u?.bio?.workHistory?.companyInfo),
    getInitialValue: (u) => u?.bio?.workHistory?.companyInfo ?? ''
  },
  headshot: {
    name: 'headshot',
    label: 'Profile photo',
    kind: 'image',
    isFilled: (u) => isNonEmptyString(u?.personal_details?.headshot),
    getInitialValue: (u) => u?.personal_details?.headshot ?? ''
  },
  // ---- Complex fields routed through existing modals/pages ----
  workHistory: {
    name: 'workHistory',
    label: 'Work history',
    kind: 'externalModal',
    externalAction: 'workHistory',
    isFilled: (u) => {
      const wh = u?.bio?.workHistory;
      if (!wh) return false;
      if (Array.isArray(wh)) {
        // Array of entries — at least one must have a non-empty companyName or role.
        return wh.some(
          (e) => isNonEmptyString(e?.companyName) || isNonEmptyString(e?.role)
        );
      }
      // Single object (BE shape) — empty strings after "remove" don't count.
      return (
        typeof wh === 'object' &&
        (isNonEmptyString((wh as any).companyName) || isNonEmptyString((wh as any).role))
      );
    }
  },
  educationHistory: {
    name: 'educationHistory',
    label: 'Education history',
    kind: 'externalModal',
    externalAction: 'educationHistory',
    isFilled: (u) => {
      const ed = u?.bio?.education;
      if (!ed) return false;
      if (Array.isArray(ed)) {
        return ed.some(
          (e) =>
            isNonEmptyString(e?.university) ||
            isNonEmptyString(e?.major) ||
            isNonEmptyString(e?.graduationDate)
        );
      }
      return (
        typeof ed === 'object' &&
        (isNonEmptyString((ed as any).university) ||
          isNonEmptyString((ed as any).major) ||
          isNonEmptyString((ed as any).graduationDate))
      );
    }
  },
  socials: {
    name: 'socials',
    label: 'Social handles',
    kind: 'externalModal',
    externalAction: 'socials',
    isFilled: (u) => {
      const s = u?.personal_details?.social;
      if (!s || typeof s !== 'object') return false;
      return Object.values(s).some((v) => isNonEmptyString(v));
    }
  },
  subjects: {
    name: 'subjects',
    label: 'Subjects (topics & industries)',
    kind: 'externalModal',
    externalAction: 'subjects',
    isFilled: (u) => isNonEmptyArray(u?.selectedTopics) && isNonEmptyArray(u?.selectedIndustries)
  },
  learningObjectives: {
    name: 'learningObjectives',
    label: 'Learning objectives',
    kind: 'externalModal',
    externalAction: 'learningObjectives',
    isFilled: (u) => isNonEmptyArray(u?.learningObjectives)
  }
};

/**
 * For one program's userOptionalFields config and the user's role, return the
 * set of field names that are mandatory for that user in that program.
 *
 * A field is mandatory when:
 *   userOptionalFields[name][role] === true
 */
export function getMandatoryFieldsForUserInGroup(
  userOptionalFields: UserOptionalFields | undefined,
  role: string
): string[] {
  if (!userOptionalFields || typeof userOptionalFields !== 'object') return [];
  const r = role as Role;
  return Object.entries(userOptionalFields)
    .filter(([, cfg]) => cfg && (cfg as RoleConfig)[r] === true)
    .map(([name]) => name);
}

/**
 * Union of mandatory field names across all the user's programs, keeping only
 * those that have a known FIELD_META entry (so we know how to render/save them).
 */
export function getUnionOfMandatoryFields(groups: UserGroupInfo[]): string[] {
  const set = new Set<string>();
  for (const g of groups) {
    for (const name of getMandatoryFieldsForUserInGroup(g.userOptionalFields, g.role)) {
      if (FIELD_META[name]) set.add(name);
    }
  }
  return Array.from(set);
}

/**
 * From the union of mandatory fields, keep only those the user hasn't filled yet.
 */
export function getMissingMandatoryFields(groups: UserGroupInfo[], user: any): FieldMeta[] {
  const union = getUnionOfMandatoryFields(groups);
  return union
    .map((name) => FIELD_META[name])
    .filter((meta) => meta && !meta.isFilled(user));
}
